import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import LabsAppBar from '../../components/LabsAppBar';
import { Contract } from '../../types/contract';
import { getContract } from '../../services/contractApiService';
import { useNotifications } from '../../contexts/NotificationContext';
import { AlertCircle } from 'lucide-react';
import ContractSummaryPanel from '../../components/contracts/ContractSummaryPanel';
import ContractStatusPill from '../../components/contracts/ContractStatusPill';

const AcceptedContractDetailsPage: React.FC = () => {
  const { contractId } = useParams<{ contractId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { showNotification } = useNotifications();

  const [contract, setContract] = useState<Contract | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const contractName = location.state?.contractName || 'Contract Details';

  const breadcrumbs = [
    { name: 'Data Contracts', iconName: 'SquareCheckBig', path: '/labs/contracts' },
    { name: contractName, iconName: 'FileText' },
  ];

  const loadContract = useCallback(async () => {
    if (!contractId) {
      setError('Contract ID is missing.');
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      const data = await getContract(contractId);
      if (data.status !== 'accepted') {
        showNotification('warning', 'This contract is not accepted.');
        navigate(`/labs/contracts/${contractId}/edit`); // Redirect if not accepted
        return;
      }
      setContract(data);
    } catch (err) {
      console.error('Failed to fetch contract details:', err);
      setError('Failed to load contract details. Please try again.');
      showNotification('error', 'Failed to load contract details.');
    } finally {
      setIsLoading(false);
    }
  }, [contractId, showNotification, navigate]);

  useEffect(() => {
    loadContract();
  }, [loadContract]);

  if (isLoading) {
    return (
      <div className="flex flex-col h-full">
        <LabsAppBar breadcrumbs={breadcrumbs} actions={[]} />
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 flex items-center justify-center">
          <p className="text-gray-500 dark:text-gray-400">Loading contract details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col h-full">
        <LabsAppBar breadcrumbs={breadcrumbs} actions={[]} />
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4 flex items-start">
            <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400 mr-3 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="flex flex-col h-full">
        <LabsAppBar breadcrumbs={breadcrumbs} actions={[]} />
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 flex items-center justify-center">
          <p className="text-gray-500 dark:text-gray-400">Contract not found.</p>
        </div>
      </div>
    );
  }

  const acceptedDate = contract.acceptedAt ? new Date(contract.acceptedAt).toLocaleString() : 'N/A';
  const acceptedBy = contract.acceptedByName || 'N/A';

  // The checked properties are stored in contract.data.checkedProperties

  // Assuming contract.data contains 'properties' and 'events' arrays
  const allEvents = contract.data?.events || [];

  return (
    <div className="flex flex-col h-full">
      <LabsAppBar
        breadcrumbs={breadcrumbs}
        actions={[]}
      />
      <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
        <div className="max-w-9xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column: Contract Details */}
          <div className="lg:col-span-2 space-y-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Contract: {contract.name}</h2>
              {contract.status && <ContractStatusPill status={contract.status as 'accepted' | 'pending' | 'draft'} />}
            </div>

            {/* Acceptance Details Card */}
            <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:px-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                  Acceptance Details
                </h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
                  Information about when and by whom this contract was accepted.
                </p>
              </div>
              <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-5 sm:p-0">
                <dl className="sm:divide-y sm:divide-gray-200 dark:sm:divide-gray-700">
                  <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Accepted Date</dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:mt-0 sm:col-span-2">{acceptedDate}</dd>
                  </div>
                  <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Accepted By</dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:mt-0 sm:col-span-2">{acceptedBy}</dd>
                  </div>
                </dl>
              </div>
            </div>

            {/* All Events Card */}
            <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:px-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                  All Contract Events
                </h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
                  A list of all events associated with this contract.
                </p>
              </div>
              <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-5 sm:p-0">
                <dl className="sm:divide-y sm:divide-gray-200 dark:sm:divide-gray-700">
                  {allEvents.length > 0 ? (
                    allEvents.map((event: any, index: number) => (
                      <div key={index} className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Event Name</dt>
                        <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:mt-0 sm:col-span-2">
                          {event.name} (ID: {event.id})
                          {event.properties && event.properties.length > 0 && (
                            <ul className="mt-2 border border-gray-200 dark:border-gray-700 rounded-md divide-y divide-gray-200 dark:divide-gray-700">
                              {event.properties.map((prop: any, propIndex: number) => (
                                <li key={propIndex} className="pl-3 pr-4 py-3 flex items-center justify-between text-sm">
                                  <div className="w-0 flex-1 flex items-center">
                                    <span className="ml-2 flex-1 w-0 truncate">{prop.name}</span>
                                  </div>
                                  <div className="ml-4 flex-shrink-0 text-gray-400 dark:text-gray-500">
                                    Path: {prop.path || 'N/A'}, Type: {prop.type || 'N/A'}
                                  </div>
                                </li>
                              ))}
                            </ul>
                          )}
                        </dd>
                      </div>
                    ))
                  ) : (
                    <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:mt-0 sm:col-span-3">No events found for this contract.</dd>
                    </div>
                  )}
                </dl>
              </div>
            </div>
          </div>

          {/* Right Column: Summary Panel */}
          <div className="lg:col-span-1">
            <ContractSummaryPanel contract={contract} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AcceptedContractDetailsPage;
